import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import {
  EMPTY_DRAFT, INITIAL_FLOW, consumePrefill, droppedUrls, excludedFromCapture, flowReducer, markSeen,
  subscribeOpenFeedback, unreadIds,
  type FeedbackCategory, type FeedbackDraft, type FlowAction, type FlowState,
} from '@/lib/feedback/core';
import { fetchFeedbackConfig, fetchMyReports, type FeedbackConfig, type MyReport } from '@/lib/feedback/api';
import { installFeedbackTelemetry, resetFeedbackTelemetry, setRouteTemplates } from '@/lib/feedback/telemetry';
// SPEC-457: the pre-fill param keys are defined ONCE, beside the builder that writes them.
import { PREFILL_DESC_PARAM, PREFILL_TITLE_PARAM } from '@/lib/feedbackEscalation';
import { FeedbackPill } from './FeedbackPill';
import { SnipOverlay } from './SnipOverlay';
import { MarkupCanvas } from './MarkupCanvas';
import { FeedbackPanel } from './FeedbackPanel';

/* SPEC-506 — the Feedback engine's shell: one provider in AppShell, so the pill, the
 * Cmd/Ctrl+Shift+F shortcut and the sidebar entry all open the SAME flow on every page.
 *
 * Snip-first order (Param's workflow, and why the panel is never in its own picture):
 *   open → 'capturing' (the page is captured; no panel exists yet, and everything marked
 *   data-feedback-ui is filtered out of the capture) → 'snip' (drag a box / whole page) →
 *   optional 'markup' → 'panel' with the thumbnail attached.
 * If capture is switched off (FEEDBACK_AUTO_CAPTURE) or fails, the panel opens on the
 * paste / choose-a-file path instead. getDisplayMedia is deliberately not used (NK).
 *
 * Openers: the pill, Cmd/Ctrl+Shift+F, and the window event OPEN_FEEDBACK_EVENT
 * ('suryaiq:open-feedback') — the rail's Feedback button (SPEC-509) fires that event, so this
 * module never imports the rail. The SPEC-457 "Request a change?" link opens it pre-filled. */

interface OpenOptions { category?: FeedbackCategory; text?: string; tab?: 'new' | 'mine' }

interface FeedbackApi {
  open: (opts?: OpenOptions) => void;
  unread: boolean;
  /** SPEC-521: this login's own reports (the same list as My feedback), for pages that show
   *  it inline — the Scenarios page's "My requests". `mineState` is 'loading' until the first
   *  read answers and 'failed' if it could not be read, so a page never says "nothing sent"
   *  before it knows. */
  mine: MyReport[];
  mineState: MineState;
}

export type MineState = 'loading' | 'ready' | 'failed';

const FeedbackContext = React.createContext<FeedbackApi>({ open: () => {}, unread: false, mine: [], mineState: 'loading' });

export function useFeedback(): FeedbackApi {
  return React.useContext(FeedbackContext);
}

// Installed at module load, so console errors from the first render are kept too.
installFeedbackTelemetry();

const SEEN_KEY = (u: string) => `suryaiq_feedback_seen_${u}`;
const CAPTURE_TIMEOUT_MS = 20_000;
const REFRESH_MS = 5 * 60_000;

function loadSeen(username: string | undefined): Record<string, string> {
  if (!username) return {};
  try { return JSON.parse(localStorage.getItem(SEEN_KEY(username)) || '{}'); } catch { return {}; }
}

async function capturePage(): Promise<{ url: string; width: number; height: number }> {
  const { domToBlob } = await import('modern-screenshot');
  const width = window.innerWidth;
  const height = window.innerHeight;
  // documentElement + restoreScrollPosition: the SPEC-506 prototype showed <body> drifts by
  // 8px and loses the inner <main> scroll position without them.
  const blob = await Promise.race([
    domToBlob(document.documentElement, {
      type: 'image/jpeg', quality: 0.85, scale: 1, width, height, timeout: 15_000,
      features: { restoreScrollPosition: true },
      filter: (node) => !excludedFromCapture(node as unknown as Element),
    }),
    new Promise<never>((_, reject) => setTimeout(() => reject(new Error('capture timed out')), CAPTURE_TIMEOUT_MS)),
  ]);
  return { url: URL.createObjectURL(blob), width, height };
}

export function FeedbackProvider({ children, chatOpen = false }: { children: React.ReactNode; chatOpen?: boolean }) {
  const { user } = useAuth();
  const username = user?.username;
  const [config, setConfig] = React.useState<FeedbackConfig | null>(null);
  const [flow, setFlow] = React.useState<FlowState>(INITIAL_FLOW);
  const [draft, setDraft] = React.useState<FeedbackDraft>(EMPTY_DRAFT);
  const [tab, setTab] = React.useState<'new' | 'mine'>('new');
  const [mine, setMine] = React.useState<MyReport[]>([]);
  const [mineState, setMineState] = React.useState<MineState>('loading');
  const [seen, setSeen] = React.useState<Record<string, string>>(() => loadSeen(username));

  // Every transition revokes the object URLs it leaves behind.
  const dispatch = React.useCallback((a: FlowAction) => {
    setFlow((prev) => {
      const next = flowReducer(prev, a);
      droppedUrls(prev, next).forEach((u) => URL.revokeObjectURL(u));
      return next;
    });
  }, []);

  React.useEffect(() => {
    // A different login in the same tab must not inherit the last person's failed requests.
    resetFeedbackTelemetry();
    // ...nor see the last person's reports (SPEC-521: the list is shown inline on pages now).
    setMine([]);
    setMineState('loading');
    if (!username) return;
    setSeen(loadSeen(username));
    fetchFeedbackConfig()
      .then((c) => { setRouteTemplates(c.routes ?? []); setConfig(c); })
      .catch(() => setConfig(null));
  }, [username]);

  // The login a read was made for; a reply that lands after a login change is dropped, so one
  // person's reports never show under another's name (SPEC-521: the list is inline on pages).
  const currentUser = React.useRef(username);
  currentUser.current = username;

  const refreshMine = React.useCallback(() => {
    if (!username) return;
    const asked = username;
    fetchMyReports()
      .then((r) => {
        if (currentUser.current !== asked) return;
        setMine(r.items || []);
        setMineState('ready');
      })
      // Keep the last list; only a list never read is reported as failed.
      .catch(() => { if (currentUser.current === asked) setMineState((st) => (st === 'ready' ? st : 'failed')); });
  }, [username]);

  React.useEffect(() => {
    refreshMine();
    const t = setInterval(refreshMine, REFRESH_MS);
    return () => clearInterval(t);
  }, [refreshMine]);

  const unread = React.useMemo(() => unreadIds(mine, seen), [mine, seen]);

  const markAllSeen = React.useCallback(() => {
    if (!username) return;
    const next = markSeen(mine);
    setSeen(next);
    try { localStorage.setItem(SEEN_KEY(username), JSON.stringify(next)); } catch { /* private mode */ }
  }, [mine, username]);

  const open = React.useCallback((opts?: OpenOptions) => {
    if (opts?.category || opts?.text) {
      setDraft({ ...EMPTY_DRAFT, category: opts.category ?? null, text: opts.text ?? '' });
    }
    const wantMine = opts?.tab === 'mine';
    setTab(wantMine ? 'mine' : 'new');
    // My feedback needs no picture of the page.
    dispatch({ type: 'open', autoCapture: !wantMine && !!config?.auto_capture });
  }, [config?.auto_capture, dispatch]);

  // The rail (SPEC-509) and any other surface open the same flow through one window event.
  React.useEffect(() => subscribeOpenFeedback(window, (d) => open({ category: d.category, text: d.text, tab: d.tab })), [open]);

  // SPEC-457: a thumbs-down's "Request a change?" link lands here with pre-fill params; they are
  // consumed once and stripped, so a refresh or back-navigation does not re-open the panel.
  const location = useLocation();
  const navigate = useNavigate();
  React.useEffect(() => {
    const pre = consumePrefill(location.search, PREFILL_TITLE_PARAM, PREFILL_DESC_PARAM);
    if (!pre) return;
    open(pre.open);
    navigate(`${location.pathname}${pre.rest ? `?${pre.rest}` : ''}`, { replace: true });
  }, [location.search, location.pathname, navigate, open]);

  // Capture runs only in 'capturing' — the panel does not exist in this phase.
  React.useEffect(() => {
    if (flow.phase !== 'capturing') return;
    let live = true;
    // Two frames: let any just-closed overlay leave the DOM before the picture is taken.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      capturePage()
        .then((c) => {
          if (live) dispatch({ type: 'captured', capture: { ...c, source: 'capture' } });
          else URL.revokeObjectURL(c.url);
        })
        .catch(() => { if (live) dispatch({ type: 'capture_failed' }); });
    }));
    return () => { live = false; };
  }, [flow.phase, dispatch]);

  // Cmd/Ctrl+Shift+F, from anywhere.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        open();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const close = React.useCallback(() => {
    dispatch({ type: 'close' });
    setDraft(EMPTY_DRAFT);
  }, [dispatch]);

  const api = React.useMemo(() => ({ open, unread: unread.length > 0, mine, mineState }),
    [open, unread.length, mine, mineState]);
  const maxPx = config?.max_px ?? 1600;

  return (
    <FeedbackContext.Provider value={api}>
      {children}
      {username && <FeedbackPill onOpen={() => open()} unread={unread.length > 0} chatOpen={chatOpen} />}
      {flow.phase === 'capturing' && (
        <div data-feedback-ui="" role="status"
          className="fixed bottom-20 right-4 md:bottom-10 md:right-5 z-[70] px-3 py-1.5 rounded-full bg-[var(--bg-overlay)] border border-[var(--border-default)] text-xs text-[var(--text-secondary)] shadow-lg">
          Taking a picture of the page…
        </div>
      )}
      {flow.phase === 'snip' && flow.capture && (
        <SnipOverlay capture={flow.capture} maxPx={maxPx}
          onDone={(shot) => dispatch({ type: 'snipped', shot })}
          onSkip={() => dispatch({ type: 'skip_shot' })} />
      )}
      {flow.phase === 'markup' && flow.shot && (
        <MarkupCanvas shot={flow.shot}
          onDone={(shot) => dispatch({ type: 'markup_done', shot })}
          onCancel={() => dispatch({ type: 'markup_cancel' })} />
      )}
      {flow.phase === 'panel' && (
        <FeedbackPanel
          flow={flow}
          dispatch={dispatch}
          config={config}
          draft={draft}
          setDraft={setDraft}
          tab={tab}
          setTab={setTab}
          mine={mine}
          unreadIds={unread}
          onOpenMine={markAllSeen}
          onSent={refreshMine}
          onClose={close}
          onSendAnother={() => { close(); setTimeout(() => open(), 0); }}
        />
      )}
    </FeedbackContext.Provider>
  );
}
